import React, { forwardRef, useState, useCallback } from "react";
import { Row } from "@/shared/ui/Blocks";
import { IconButton } from "@/shared/ui/IconButton";
import { cn } from "@/shared/utils/shadcn";

const MAX_HEIGHT = 300;

/**
 * Resizes textarea to fit content, capped at MAX_HEIGHT
 */
const resizeTextarea = (textarea: HTMLTextAreaElement) => {
  textarea.style.height = "auto";
  const newHeight = Math.min(textarea.scrollHeight, MAX_HEIGHT);
  textarea.style.height = `${newHeight}px`;
};

export interface ChatTextInputProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onIconClick: () => void;
}

export const ChatTextInput = forwardRef<
  HTMLTextAreaElement,
  ChatTextInputProps
>(({ onIconClick, onInput, onKeyDown, ...props }, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Combine internal ref with forwarded ref
  React.useImperativeHandle(ref, () => textareaRef.current!);

  const handleSubmit = useCallback(() => {
    const hasContent = (textareaRef.current?.value.trim().length ?? 0) > 0;
    if (hasContent) {
      onIconClick();
      // Reset height after submit (value gets cleared by parent)
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  }, [onIconClick]);

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>) => {
      resizeTextarea(e.currentTarget);
      onInput?.(e);
    },
    [onInput]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter submits (if not empty), Shift+Enter adds newline
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const hasContent = e.currentTarget.value.trim().length > 0;
        if (hasContent) {
          handleSubmit();
        }
      }
      onKeyDown?.(e);
    },
    [handleSubmit, onKeyDown]
  );

  return (
    <Row
      className={cn(
        "w-full z-[9999] items-end border border-solid rounded-md transition-colors bg-white py-2",
        isFocused ? "border-purple-500" : "border-grey-300"
      )}
    >
      <IconButton
        icon="Send"
        color={isFocused ? "purple" : "grey"}
        variant="ghost"
        onClick={handleSubmit}
        size="md"
        className="mx-2 shrink-0"
        type="button"
      />
      <textarea
        ref={textareaRef}
        className="flex-1 text-sm focus:outline-none border-none ml-0 resize-none overflow-y-auto max-h-[300px] pr-2 pt-2 pb-2 self-end"
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        rows={1}
        style={{ minHeight: "38px" }}
        {...props}
      />
    </Row>
  );
});
